// src/components/income/IncomeForm.tsx
import React from 'react';
import { Save, X, RefreshCw } from 'lucide-react';
import { IncomeData } from '../../pages/Income'; // Adjust path
import { LookupValue } from '../../services/lookupService'; // Adjust path

interface IncomeFormProps {
  formData: IncomeData;
  editingId: string | null;
  isFormSubmitted: boolean;
  dropdownOptions: {
    ownerType: LookupValue[];
    incomeType: LookupValue[];
    incomeSubType: LookupValue[];
    incomeFrequency: LookupValue[];
  };
  dependents: { id: string; name: string }[];
  loadingSubTypes: boolean;
  handleSubmit: (e: React.FormEvent) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onCancel: () => void;
}

const renderDropdownOptions = (options: LookupValue[], placeholder = 'Select...') => (
  <>
    <option value="">{placeholder}</option>
    {options && options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </>
);

const IncomeForm: React.FC<IncomeFormProps> = ({
  formData, editingId, isFormSubmitted, dropdownOptions, dependents, loadingSubTypes,
  handleSubmit, handleInputChange, handleSelectChange, onCancel
}) => {
  return (
    <div className="p-6 bg-white">
      <h3 className="text-xl font-semibold text-gray-900 mb-4 md:mb-6">{editingId ? 'Edit Income' : 'Add New Income'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Owner Type *</label>
            <select name="incomeOwnerType" value={formData.incomeOwnerType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
              {renderDropdownOptions(dropdownOptions.ownerType, 'Select Owner Type')}
            </select>
          </div>
          {formData.incomeOwnerType === '2' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Family Member *</label>
              <select name="familyMemberId" value={formData.familyMemberId} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                <option value="">Select Family Member</option>
                {dependents.map(dep => <option key={dep.id} value={dep.id}>{dep.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
            <input type="text" name="incomeName" value={formData.incomeName} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
            <select name="incomeType" value={formData.incomeType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
              {renderDropdownOptions(dropdownOptions.incomeType, 'Select Type')}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sub Type</label>
            <select name="incomeSubType" value={formData.incomeSubType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" disabled={loadingSubTypes}>
              {loadingSubTypes ? <option>Loading...</option> : renderDropdownOptions(dropdownOptions.incomeSubType, formData.incomeType ? 'Select Sub Type' : 'Select Type first')}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
            <input type="number" name="incomeAmount" value={formData.incomeAmount} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
            <select name="incomeFrequency" value={formData.incomeFrequency} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm">
              {renderDropdownOptions(dropdownOptions.incomeFrequency, 'Select Frequency')}
            </select>
          </div>
           <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Total Annual Income</label>
            <input type="number" name="totalIncomeAmount" value={formData.totalIncomeAmount} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Total Family Income</label>
            <input type="number" name="totalFamilyIncome" value={formData.totalFamilyIncome} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>
        <div className="flex flex-col-reverse md:flex-row justify-end space-y-reverse space-y-2 md:space-y-0 md:space-x-4 mt-6">
          <button type="button" onClick={onCancel} className="bg-red-500 text-white py-2 px-4 md:px-6 md:py-3 rounded-lg hover:bg-red-600 flex items-center justify-center">
            <X className="w-4 h-4 mr-2" />Cancel
          </button>
          <button type="submit" className="px-4 py-2 md:px-6 md:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center" disabled={isFormSubmitted}>
            {isFormSubmitted ? <RefreshCw className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin" /> : <Save className="w-4 h-4 md:w-5 md:h-5 mr-2" />}
            {isFormSubmitted ? 'Loading' : editingId ? 'Update Income' : 'Add Income'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IncomeForm;